/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { Injectable, effect, inject, signal } from "@angular/core";
import { CbRoutesUtils } from "@app/@classes/routes/cb-routes-utils";
import { Supabase } from "@app/@classes/supabase";
import { Subject, catchError, from, map, of, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthStateService } from "./auth-state.service";
import { LoggerService } from "./logger.service";

export type CbFeature = {
	title: string;
	description: string;
	icon: string;
	path: string;
	canAccess: () => boolean;
};

@Injectable({
	providedIn: "root",
})
export class FeaturesService extends Supabase {
	private readonly loggerService = inject(LoggerService);
	private readonly authStateService = inject(AuthStateService);

	private readonly allowFullAccessInDevelopment = false;

	public features: Record<string, CbFeature> = {};

	protected readonly destroy$ = new Subject<void>();

	private _userFeatureAccess: Record<
		string,
		{ select: boolean; insert: boolean; update: boolean; delete: boolean }
	> = {};

	public isLoading = signal<boolean>(false);

	constructor() {
		super();

		effect(() => {
			this.loadPrivileges().subscribe();
		});

		this.features = {
			w_home: {
				icon: "cb-base-bold-home",
				title: "SECTIONS.ROUTES.HOME",
				description: "HOME.APPS.HOME.DESCRIPTION",
				path: CbRoutesUtils.paths.w.home.path,
				canAccess: () => true,
			},
			a_home: {
				icon: "cb-base-bold-home",
				title: "SECTIONS.ROUTES.HOME",
				description: "HOME.APPS.HOME.DESCRIPTION",
				path: CbRoutesUtils.paths.a.home.path,
				canAccess: () => true,
			},
			p_home: {
				icon: "cb-base-bold-home",
				title: "SECTIONS.ROUTES.HOME",
				description: "HOME.APPS.HOME.DESCRIPTION",
				path: CbRoutesUtils.paths.p.home.path,
				canAccess: () => true,
			},
			products: {
				icon: "cb-app-bold-package-open",
				title: "SECTIONS.ROUTES.PRODUCTS",
				description: "HOME.APPS.PRODUCTS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.products.path,
				canAccess: () => true,
			},
			warehouseManagement: {
				icon: "cb-app-bold-package-moving",
				title: "SECTIONS.ROUTES.WAREHOUSE_MANAGEMENT",
				description: "HOME.APPS.WAREHOUSE_MANAGEMENT.DESCRIPTION",
				path: CbRoutesUtils.paths.w.warehouse.management.path,
				canAccess: () => true,
			},
			incomingOrders: {
				icon: "cb-app-bold-truck-return",
				title: "SECTIONS.ROUTES.INCOMING_ORDERS",
				description: "HOME.APPS.INCOMING_ORDERS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.orders.incoming.path,
				canAccess: () => true,
			},
			outgoingOrders: {
				icon: "cb-app-bold-route-03",
				title: "SECTIONS.ROUTES.OUTGOING_ORDERS",
				description: "HOME.APPS.OUTGOING_ORDERS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.orders.outgoing.path,
				canAccess: () => true,
			},
			w_companies: {
				icon: "cb-app-bold-building-03",
				title: "SECTIONS.ROUTES.COMPANIES",
				description: "HOME.APPS.COMPANIES.DESCRIPTION",
				path: CbRoutesUtils.paths.w.companies.path,
				canAccess: () => true,
			},
			p_companies: {
				icon: "cb-app-bold-building-03",
				title: "SECTIONS.ROUTES.COMPANIES",
				description: "HOME.APPS.COMPANIES.DESCRIPTION",
				path: CbRoutesUtils.paths.p.companies.path,
				canAccess: () => true,
			},
			w_emails: {
				icon: "cb-app-bold-mail",
				title: "SECTIONS.ROUTES.EMAILS",
				description: "HOME.APPS.EMAILS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.email.path,
				canAccess: () => !environment.production,
			},
			a_emails: {
				icon: "cb-app-bold-mail",
				title: "SECTIONS.ROUTES.EMAILS",
				description: "HOME.APPS.EMAILS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.email.path,
				canAccess: () => !environment.production,
			},
			warehouse: {
				icon: "cb-app-bold-warehouse",
				title: "SECTIONS.ROUTES.WAREHOUSE",
				description: "HOME.APPS.WAREHOUSE.DESCRIPTION",
				path: CbRoutesUtils.paths.w.warehouse.layout.path,
				canAccess: () => true,
			},
			w_profile: {
				icon: "cb-base-outline-preferences",
				title: "SECTIONS.ROUTES.PROFILE",
				description: "",
				path: CbRoutesUtils.paths.w.settings.user.path,
				canAccess: () => true,
			},
			a_profile: {
				icon: "cb-base-outline-preferences",
				title: "SECTIONS.ROUTES.PROFILE",
				description: "",
				path: CbRoutesUtils.paths.a.settings.user.path,
				canAccess: () => true,
			},
			p_profile: {
				icon: "cb-base-outline-preferences",
				title: "SECTIONS.ROUTES.PROFILE",
				description: "",
				path: CbRoutesUtils.paths.p.settings.user.path,
				canAccess: () => true,
			},
			w_tenant: {
				icon: "cb-base-outline-tenant",
				title: "SECTIONS.ROUTES.TENANT",
				description: "",
				path: CbRoutesUtils.paths.w.settings.tenant.path,
				canAccess: () => false /* this.isOwner || this.isAdmin */,
			},
			a_tenant: {
				icon: "cb-base-outline-tenant",
				title: "SECTIONS.ROUTES.TENANT",
				description: "",
				path: CbRoutesUtils.paths.a.settings.tenant.path,
				canAccess: () => false /* this.isOwner || this.isAdmin */,
			},
			p_tenant: {
				icon: "cb-base-outline-tenant",
				title: "SECTIONS.ROUTES.TENANT",
				description: "",
				path: CbRoutesUtils.paths.p.settings.tenant.path,
				canAccess: () => false /* this.isOwner || this.isAdmin */,
			},
			w_collaborators: {
				icon: "cb-base-outline-users-check",
				title: "SECTIONS.ROUTES.COLLABORATORS",
				description: "",
				path: CbRoutesUtils.paths.w.settings.collaborators.path,
				canAccess: () => this.canSelect(["collaborators"], "or"),
			},
			a_collaborators: {
				icon: "cb-base-outline-users-check",
				title: "SECTIONS.ROUTES.COLLABORATORS",
				description: "",
				path: CbRoutesUtils.paths.a.settings.collaborators.path,
				canAccess: () => this.canSelect(["collaborators"], "or"),
			},
			p_collaborators: {
				icon: "cb-base-outline-users-check",
				title: "SECTIONS.ROUTES.COLLABORATORS",
				description: "",
				path: CbRoutesUtils.paths.p.settings.collaborators.path,
				canAccess: () => this.canSelect(["collaborators"], "or"),
			},
			w_settings: {
				icon: "cb-base-bold-settings",
				title: "SECTIONS.ROUTES.SETTINGS",
				description: "HOME.APPS.SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.settings.path,
				canAccess: () => true,
			},
			a_settings: {
				icon: "cb-base-bold-settings",
				title: "SECTIONS.ROUTES.SETTINGS",
				description: "HOME.APPS.SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.path,
				canAccess: () => true,
			},
			p_settings: {
				icon: "cb-base-bold-settings",
				title: "SECTIONS.ROUTES.SETTINGS",
				description: "HOME.APPS.SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.p.settings.path,
				canAccess: () => true,
			},
			tags: {
				icon: "cb-app-outline-tags",
				title: "SECTIONS.ROUTES.TAGS",
				description: "HOME.APPS.SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.settings.tags.path,
				canAccess: () => true,
			},
			pallets_tags: {
				icon: "cb-app-outline-package-moving",
				title: "SECTIONS.ROUTES.PALLETS_TAGS",
				description: "HOME.APPS.SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.settings.tags.pallets_tags.path,
				canAccess: () => true,
			},
			orders_tags: {
				icon: "cb-app-outline-truck-return",
				title: "SECTIONS.ROUTES.ORDERS_TAGS",
				description: "HOME.APPS.SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.settings.tags.orders_tags.path,
				canAccess: () => true,
			},
			articles_tags: {
				icon: "cb-app-outline-package-open",
				title: "SECTIONS.ROUTES.ARTICLES_TAGS",
				description: "HOME.APPS.SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.settings.tags.articles_tags.path,
				canAccess: () => true,
			},
			palmtop: {
				icon: "cb-app-bold-smart-phone-01",
				title: "SECTIONS.ROUTES.PALM_TOP",
				description: "HOME.APPS.PALM_TOP.DESCRIPTION",
				path: CbRoutesUtils.paths.w.palmtop.path,
				canAccess: () => true,
			},
			works: {
				icon: "cb-app-bold-wrench",
				title: "SECTIONS.ROUTES.WORKS",
				description: "HOME.APPS.WORKS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.works.path,
				canAccess: () => true,
			},
			alerts: {
				icon: "cb-app-bold-spam",
				title: "SECTIONS.ROUTES.ALERTS",
				description: "HOME.APPS.ALERTS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.alerts.path,
				canAccess: () => true,
			},
			palmtop_operations: {
				icon: "cb-app-bold-smart-phone-01",
				title: "SECTIONS.ROUTES.PALMTOP_OPERATIONS",
				description: "HOME.APPS.PALMTOP_OPERATIONS.DESCRIPTION",
				path: CbRoutesUtils.paths.w.palmtop_operations.path,
				canAccess: () => true,
			},
			a_companies: {
				icon: "cb-app-bold-building-03",
				title: "SECTIONS.ROUTES.COMPANIES",
				description: "HOME.APPS.COMPANIES.DESCRIPTION",
				path: CbRoutesUtils.paths.a.companies.path,
				canAccess: () => true,
			},
			vehicles: {
				icon: "cb-app-bold-container-truck",
				title: "SECTIONS.ROUTES.VEHICLES",
				description: "HOME.APPS.VEHICLES.DESCRIPTION",
				path: CbRoutesUtils.paths.a.vehicles.path,
				canAccess: () => true,
			},
			warehouse_price_list: {
				icon: "cb-app-outline-invoice",
				title: "SECTIONS.ROUTES.WAREHOUSE_PRICE_LIST",
				description: "HOME.APPS.WAREHOUSE_PRICE_LIST.DESCRIPTION",
				path: CbRoutesUtils.paths.w.settings.price_list.path,
				canAccess: () => true,
			},
			invoice_settings: {
				icon: "cb-app-outline-invoice-03",
				title: "SECTIONS.ROUTES.INVOICE_SETTINGS",
				description: "HOME.APPS.INVOICE_SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings.path,
				canAccess: () => true,
			},
			payment_conditions: {
				icon: "cb-app-outline-money-exchange-02",
				title: "SECTIONS.ROUTES.PAYMENT_CONDITIONS",
				description: "HOME.APPS.PAYMENT_CONDITIONS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings.payment_conditions
					.path,
				canAccess: () => true,
			},
			vat_codes: {
				icon: "cb-app-outline-percent-02",
				title: "SECTIONS.ROUTES.VAT_CODES",
				description: "HOME.APPS.VAT_CODES.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings.vat_codes.path,
				canAccess: () => true,
			},
			measure_units: {
				icon: "cb-app-outline-tape-measure",
				title: "SECTIONS.ROUTES.MEASURE_UNITS",
				description: "HOME.APPS.MEASURE_UNITS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings.measure_units
					.path,
				canAccess: () => true,
			},
			tenant_invoice_parameters: {
				icon: "cb-app-bold-building-03",
				title: "SECTIONS.ROUTES.TENANT_INVOICE_PARAMETERS",
				description: "HOME.APPS.TENANT_INVOICE_PARAMETERS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings
					.tenant_invoice_parameters.path,
				canAccess: () => true,
			},
			document_types: {
				icon: "cb-app-outline-file-02",
				title: "SECTIONS.ROUTES.DOCUMENT_TYPES",
				description: "HOME.APPS.DOCUMENT_TYPES.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings.document_types
					.path,
				canAccess: () => true,
			},
			bank_cash: {
				icon: "cb-app-outline-piggy-bank",
				title: "SECTIONS.ROUTES.BANK_CASH",
				description: "HOME.APPS.BANK_CASH.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings.bank_cash.path,
				canAccess: () => true,
			},
			invoice_server_settings: {
				icon: "cb-base-outline-settings",
				title: "SECTIONS.ROUTES.INVOICE_SERVER_SETTINGS",
				description: "HOME.APPS.INVOICE_SERVER_SETTINGS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.settings.invoice_settings
					.invoice_server_settings.path,
				canAccess: () => true,
			},
			email_settings: {
				icon: "cb-base-outline-email",
				title: "SECTIONS.ROUTES.EMAIL_SETTINGS",
				description: "",
				path: CbRoutesUtils.paths.w.settings.email_settings.path,
				canAccess: () => !environment.production,
			},
			inventory: {
				icon: "cb-app-bold-delivery-view",
				title: "SECTIONS.ROUTES.INVENTORY",
				description: "HOME.APPS.INVENTORY.DESCRIPTION",
				path: CbRoutesUtils.paths.w.inventory.path,
				canAccess: () => true,
			},
			invoices: {
				icon: "cb-app-bold-invoice-02",
				title: "SECTIONS.ROUTES.INVOICES",
				description: "HOME.APPS.INVOICES.DESCRIPTION",
				path: CbRoutesUtils.paths.a.invoices.list.path,
				canAccess: () => true,
			},
			calendar: {
				icon: "cb-app-bold-calendar-03",
				title: "SECTIONS.ROUTES.CALENDAR",
				description: "HOME.APPS.CALENDAR.DESCRIPTION",
				path: CbRoutesUtils.paths.a.calendar.path,
				canAccess: () => true,
			},
			staff_members: {
				icon: "cb-app-bold-user-group",
				title: "SECTIONS.ROUTES.STAFF_MEMBERS",
				description: "HOME.APPS.STAFF_MEMBERS.DESCRIPTION",
				path: CbRoutesUtils.paths.a.staff_members.path,
				canAccess: () => true,
			},
			pickups: {
				icon: "cb-app-bold-navigation-03",
				title: "SECTIONS.ROUTES.PICKUPS",
				description: "HOME.APPS.PICKUPS.DESCRIPTION",
				path: CbRoutesUtils.paths.p.pickups.list.path,
				canAccess: () => true,
			},
			pre_billing: {
				icon: "cb-app-bold-invoice-04",
				title: "SECTIONS.ROUTES.PRE_BILLING",
				description: "HOME.APPS.PRE_BILLING.DESCRIPTION",
				path: CbRoutesUtils.paths.a.pre_billing.path,
				canAccess: () => true,
			},
		};
	}

	public canSelect(
		featureNames: string[],
		aggregation: "or" | "and" = "or",
	): boolean {
		if (!environment.production && this.allowFullAccessInDevelopment)
			return true;

		if (aggregation === "or") {
			return featureNames.some(
				(featureName) => this._userFeatureAccess[featureName]?.select,
			);
		}

		return featureNames.every(
			(featureName) => this._userFeatureAccess[featureName]?.select,
		);
	}

	public canInsert(
		featureNames: string[],
		aggregation: "or" | "and" = "or",
	): boolean {
		if (!environment.production && this.allowFullAccessInDevelopment)
			return true;

		if (aggregation === "or") {
			return featureNames.some(
				(featureName) => this._userFeatureAccess[featureName]?.insert,
			);
		}

		return featureNames.every(
			(featureName) => this._userFeatureAccess[featureName]?.insert,
		);
	}

	public canUpdate(
		featureNames: string[],
		aggregation: "or" | "and" = "or",
	): boolean {
		if (!environment.production && this.allowFullAccessInDevelopment)
			return true;
		if (aggregation === "or") {
			return featureNames.some(
				(featureName) => this._userFeatureAccess[featureName]?.update,
			);
		}
		return featureNames.every(
			(featureName) => this._userFeatureAccess[featureName]?.update,
		);
	}

	public canDelete(
		featureNames: string[],
		aggregation: "or" | "and" = "or",
	): boolean {
		if (!environment.production && this.allowFullAccessInDevelopment)
			return true;

		if (aggregation === "or") {
			return featureNames.some(
				(featureName) => this._userFeatureAccess[featureName]?.delete,
			);
		}

		return featureNames.every(
			(featureName) => this._userFeatureAccess[featureName]?.delete,
		);
	}

	public getAccessType(featureName: string): "none" | "read" | "write" {
		if (
			this.canInsert([featureName]) ||
			this.canUpdate([featureName]) ||
			this.canDelete([featureName])
		) {
			return "write";
		}

		if (this.canSelect([featureName])) {
			return "read";
		}

		return "none";
	}

	public loadPrivileges() {
		const tenantId = this.authStateService.currentTenantId();
		const userId = this.authStateService.currentUserId();

		if (!tenantId || !userId) {
			return of([]);
		}

		this.isLoading.set(true);

		return from(
			this.supabaseClient
				.from("privileges_augmented")
				.select("*")
				.eq("tenant_id", tenantId)
				.eq("user_id", userId),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			tap((resp) => {
				if (!resp.data) return;

				this._userFeatureAccess = resp.data.reduce(
					(acc: typeof this._userFeatureAccess, curr) => {
						if (curr.feature_name) {
							acc[curr.feature_name] = {
								select: curr.select_flag ?? false,
								insert: curr.insert_flag ?? false,
								update: curr.update_flag ?? false,
								delete: curr.delete_flag ?? false,
							};
						}
						return acc;
					},
					{},
				);
			}),
			map((resp) => {
				if (resp.error) throw resp.error;

				this.loggerService.logInfo(
					"Loaded privileges: ",
					this._userFeatureAccess,
				);

				return resp.data;
			}),
			tap(() => {
				this.isLoading.set(false);
			}),
		);
	}

	/**
	 * Cleans up resources and completes the destroy$ subject.
	 */
	public onDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
